import * as React from 'react'

import type { DailyAiEvolutionRow } from '@sdr-agend/admin-schemas'

/**
 * "Evolução diária da IA" (Story 10.8, 2026-07-30, user request, AC6) —
 * gráfico de linha multi-série (5 séries: Leads/Qualificados/Agendamentos/
 * No-show/Vendas), SVG puro (sem lib de gráfico — mesma convenção dos outros
 * componentes desta página). Paleta categórica em ordem fixa (skill
 * `dataviz`), legenda sempre presente (>= 2 séries), sem rótulo em cada
 * ponto (só a legenda identifica a série).
 *
 * 2026-08-04 (user request) — eixo X ganhou rótulos de dia (`dd/mm`), pra dar
 * uma visão geral do mês conforme os dias passam, no lugar dos cards fixos
 * "Melhor dia"/"Pior dia" (removidos — a mesma informação já dava pra ler no
 * gráfico, e o eixo com todos os dias é estritamente mais informativo).
 * Rótulos são amostrados (não um por dia) pra não empilhar texto ilegível em
 * períodos longos (ex.: mês inteiro, ~30 pontos) — sempre inclui o primeiro e
 * o último dia do período.
 */
export interface DailyAiEvolutionChartProps {
  data: ReadonlyArray<DailyAiEvolutionRow>
}

interface Series {
  key: keyof Pick<DailyAiEvolutionRow, 'leadsReceived' | 'qualified' | 'scheduled' | 'noShow' | 'sales'>
  label: string
  strokeHex: string
  colorClass: string
}

const SERIES: Series[] = [
  { key: 'leadsReceived', label: 'Leads Recebidos', strokeHex: '#3b82f6', colorClass: 'bg-blue-500' },
  { key: 'qualified', label: 'Qualificados', strokeHex: '#8b5cf6', colorClass: 'bg-violet-500' },
  { key: 'scheduled', label: 'Agendamentos', strokeHex: '#f59e0b', colorClass: 'bg-amber-500' },
  // 2026-08-11 (user request) — série de no-show, por data do AGENDAMENTO
  // (`meeting.scheduled_at`), não da criação do lead — ver comentário em
  // `queryDailyAiEvolution` (dashboard.ts) sobre o eixo de data divergente.
  { key: 'noShow', label: 'No-show', strokeHex: '#ef4444', colorClass: 'bg-red-500' },
  { key: 'sales', label: 'Vendas', strokeHex: '#10b981', colorClass: 'bg-emerald-500' },
]

const WIDTH = 600
const HEIGHT = 220
const PADDING = 24
/** Espaço reservado abaixo da linha de base para os rótulos de dia do eixo X. */
const AXIS_LABEL_HEIGHT = 20
/** Teto de rótulos visíveis, independente da duração do período — evita texto empilhado/ilegível. */
const MAX_AXIS_LABELS = 8

function formatDay(iso: string): string {
  const [, month, day] = iso.split('-')
  return `${day}/${month}`
}

/**
 * Escolhe um subconjunto de índices pra rotular no eixo X — sempre o
 * primeiro e o último dia, com um passo uniforme entre eles, respeitando
 * `MAX_AXIS_LABELS`. Um dia por índice em vez de rotular todos evita
 * sobreposição de texto em períodos longos (mês inteiro ≈ 30 pontos).
 */
function pickAxisLabelIndices(length: number): number[] {
  if (length <= 1) return length === 1 ? [0] : []
  const step = Math.max(1, Math.ceil((length - 1) / (MAX_AXIS_LABELS - 1)))
  const indices: number[] = []
  for (let i = 0; i < length; i += step) indices.push(i)
  const last = length - 1
  if (indices[indices.length - 1] !== last) indices.push(last)
  return indices
}

export function DailyAiEvolutionChart({ data }: DailyAiEvolutionChartProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="daily-ai-evolution-empty"
      >
        Nenhuma atividade no período selecionado.
      </div>
    )
  }

  const maxValue = Math.max(
    1,
    ...data.flatMap((r) => [r.leadsReceived, r.qualified, r.scheduled, r.noShow, r.sales]),
  )
  const plotWidth = WIDTH - 2 * PADDING
  const plotHeight = HEIGHT - 2 * PADDING - AXIS_LABEL_HEIGHT
  const stepX = data.length > 1 ? plotWidth / (data.length - 1) : 0
  const baselineY = PADDING + plotHeight

  function pointsFor(key: Series['key']): string {
    return data
      .map((row, i) => {
        const x = PADDING + i * stepX
        const y = PADDING + plotHeight - (row[key] / maxValue) * plotHeight
        return `${x},${y}`
      })
      .join(' ')
  }

  const axisLabelIndices = pickAxisLabelIndices(data.length)

  return (
    <div data-testid="daily-ai-evolution-chart">
      <svg
        width="100%"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Evolução diária: ${SERIES.map((s) => s.label).join(', ')}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Linha de base recessiva (grid mínimo). */}
        <line
          x1={PADDING}
          y1={baselineY}
          x2={WIDTH - PADDING}
          y2={baselineY}
          className="stroke-muted"
          strokeWidth={1}
        />
        {SERIES.map((s) => (
          <polyline
            key={s.key}
            points={pointsFor(s.key)}
            fill="none"
            stroke={s.strokeHex}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            data-testid={`daily-ai-evolution-line-${s.key}`}
          />
        ))}
        {axisLabelIndices.map((i) => {
          const row = data[i] as DailyAiEvolutionRow
          const x = PADDING + i * stepX
          return (
            <text
              key={row.day}
              x={x}
              y={baselineY + 14}
              textAnchor="middle"
              className="fill-muted-foreground text-[9px]"
              data-testid={`daily-ai-evolution-axis-label-${row.day}`}
            >
              {formatDay(row.day)}
            </text>
          )
        })}
      </svg>

      <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
        {SERIES.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5" data-testid={`daily-ai-evolution-legend-${s.key}`}>
            <span className={`inline-block h-2 w-2 rounded-full ${s.colorClass}`} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export default DailyAiEvolutionChart
